import { useEffect, useState, useRef, type FC, type ReactNode } from 'react';
import LoadingSpinner from '../../../../LoadingSpinner/LoadingSpinner';
import style from './FilterWrapper.module.scss?module';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faMinus } from '@fortawesome/pro-regular-svg-icons/faMinus';
import FilterActiveTags from '../FilterActiveTags/FilterActiveTags';
import { ArrayFilterKeys } from '../../../../../Context/ProductsFilterContext';

type FilterWrapperPropT = {
  children: ReactNode;
  isSuccess: boolean;
  isPending: boolean;
  isFetching: boolean;
  resultsCount: number | undefined;
  label?: string;
  initialOpen?: boolean;
  expandable?: boolean;
  collapsedHeight?: number;
  activeFilterKeys?: ArrayFilterKeys[];
  noBorderBottom?: boolean;
};

const FilterWrapper: FC<FilterWrapperPropT> = ({
  isSuccess,
  isPending,
  isFetching,
  resultsCount,
  label,
  children,
  initialOpen = true,
  expandable = false,
  collapsedHeight = 0,
  activeFilterKeys,
  noBorderBottom = false,
}) => {
  const [showFilterLabel, setShowFilterLabel] = useState(true);
  const [isOpen, setIsOpen] = useState<boolean>(initialOpen);
  const [isExpanded, setIsExpanded] = useState<boolean>(expandable);

  useEffect(() => {
    let hasResults =
      !isFetching &&
      !isPending &&
      isSuccess &&
      resultsCount !== undefined &&
      resultsCount < 1;

    setShowFilterLabel(!hasResults);
  }, [isSuccess, isPending, isFetching, resultsCount]);

  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (contentRef.current) {
      const containerHeight = contentRef.current.scrollHeight;
      setContentHeight(containerHeight);
    }
  }, [children, isSuccess, resultsCount]);

  // Enable accordion when expandable prop is true
  useEffect(() => {
    setIsExpanded(expandable);
  }, [expandable]);

  const toggleAccordion = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div
      className={clsx(
        'd-flex flex-column gap-0 gap-lg-3',
        style.FilterWrapperContainer,
        noBorderBottom && style.noBorderBottom
      )}
    >
      {showFilterLabel && (
        <div
          className={clsx(
            style.FilterNameContainer,
            isExpanded && style.expandable,
            isExpanded && isOpen && style.open
          )}
          onClick={isExpanded ? toggleAccordion : undefined}
        >
          <div className="d-flex align-items-center">
            <div>{label}</div>

            <div className={style.FilterNameSpinner}>
              {(isPending || isFetching) && <LoadingSpinner size="sm" />}
            </div>
          </div>

          <div>
            {isExpanded && (
              <button
                className={style.ToggleIcon}
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleAccordion();
                }}
              >
                <FontAwesomeIcon icon={isOpen ? faMinus : faPlus} />
              </button>
            )}
          </div>
        </div>
      )}

      {activeFilterKeys && (
        <div className="d-lg-none">
          <FilterActiveTags filterKeys={activeFilterKeys} />
        </div>
      )}

      <div
        style={{
          opacity: isPending || isFetching ? 0.5 : 1,
          pointerEvents: isPending || isFetching ? 'none' : 'all',
          maxHeight: !isExpanded
            ? 'none'
            : isOpen
            ? contentHeight
              ? // ? `${contentHeight}px`
                '4000px'
              : 'none'
            : collapsedHeight,
          overflow: isExpanded && !isOpen ? 'auto' : 'hidden',
        }}
        className={clsx(
          style.FilterOptionsContainer,
          (!isExpanded || isOpen) && style.open,
          'CustomScrollbar'
        )}
      >
        <div
          className={clsx(
            style.OverflowHiddenInner,
            isExpanded && style.expandable
          )}
          ref={contentRef}
        >
          {isSuccess && children}
        </div>
      </div>
    </div>
  );
};

export default FilterWrapper;
